// The staff dashboard's widgets: which exist, who may see each one, and each person's saved choice of them.
// The server stores a person's layout (/api/dashboard/layout); both sides clean it with cleanDashboardLayout so a
// widget someone no longer has the permission for simply disappears.
import { matchesPermission, type PermissionRequirement } from './permissions'

export const DASHBOARD_WIDGET_TYPES = ['stats', 'recent_projects', 'my_tasks', 'task_feed', 'my_week', 'leave_approvals', 'debtors', 'photos', 'epc_requests'] as const
export type DashboardWidgetType = typeof DASHBOARD_WIDGET_TYPES[number]
export type DashboardWidth = 'wide' | 'narrow'

export interface DashboardWidgetDef {
  type: DashboardWidgetType
  label: string
  description: string
  /** Any one of these permissions shows the widget (super admins have them all). */
  perm: PermissionRequirement
  width: DashboardWidth
  /** Can be added more than once, each copy with its own filter (task feeds). */
  multiple?: boolean
  /** Only for people who approve someone's holiday, or HR managers. */
  approverOnly?: boolean
}

const TASKS = ['tasks.view', 'tasks.manage'] as const

export const DASHBOARD_WIDGETS: readonly DashboardWidgetDef[] = [
  { type: 'stats', label: 'Today’s numbers', description: 'Active projects, photos to check, photos this week and EPCs lodged.', perm: 'projects.view', width: 'wide' },
  { type: 'recent_projects', label: 'Recently updated projects', description: 'The eight projects changed most recently.', perm: 'projects.view', width: 'wide' },
  { type: 'my_tasks', label: 'My tasks due', description: 'Your overdue tasks and those due in the next 7 days.', perm: TASKS, width: 'narrow' },
  { type: 'task_feed', label: 'Task feed', description: 'Open tasks for one workstream or keyword, e.g. SAP or energy statements. Add as many as you like.', perm: TASKS, width: 'narrow', multiple: true },
  { type: 'my_week', label: 'My week', description: 'This week at a glance: your holidays, bank holidays, company events and tasks due.', perm: ['hr.self', ...TASKS], width: 'wide' },
  { type: 'leave_approvals', label: 'Holiday requests to approve', description: 'Requests waiting for your decision.', perm: ['hr.self', 'hr.manage'], width: 'narrow', approverOnly: true },
  { type: 'debtors', label: 'Outstanding invoices', description: 'Money owed to Briary, by age, and the biggest debtors.', perm: 'invoices.view', width: 'narrow' },
  { type: 'photos', label: 'Photos to check', description: 'Site photos and photo reports waiting for a decision.', perm: 'photos.review', width: 'narrow' },
  { type: 'epc_requests', label: 'EPC requests waiting', description: 'Open EPC requests, oldest first.', perm: 'epc.view', width: 'narrow' },
]
export const dashboardWidgetDef = (type: DashboardWidgetType) => DASHBOARD_WIDGETS.find((w) => w.type === type)!

export interface TaskFeedFilter {
  title: string
  workstreamId: number | null
  /** Words matched against the task title, project and client. */
  search: string
  assignee: 'me' | 'anyone'
  due: 'any' | 'overdue' | 'week'
}
export interface DashboardWidget {
  /** Stable id for this copy of the widget (task feeds can appear several times). */
  key: string
  type: DashboardWidgetType
  width: DashboardWidth
  feed?: TaskFeedFilter
}
export interface DashboardLayoutResponse {
  widgets: DashboardWidget[]
  /** False until the person saves their own layout. */
  saved: boolean
  available: DashboardWidgetType[]
  workstreams: { id: number; name: string; colour_hex: string | null }[]
}

export const DASHBOARD_MAX_WIDGETS = 24
export const DASHBOARD_MAX_TASK_FEEDS = 8
export const DASHBOARD_KEY = /^[a-z0-9_-]{1,40}$/i

export interface DashboardAccess {
  permissions: readonly string[]
  isSuperadmin: boolean
  /** Named as someone's line manager or holiday approver. */
  approver: boolean
}

/** The widgets this person may see. */
export function availableDashboardWidgets(a: DashboardAccess): DashboardWidgetType[] {
  return DASHBOARD_WIDGETS.filter((w) => {
    if (!a.isSuperadmin && !matchesPermission(a.permissions, w.perm)) return false
    if (w.approverOnly && !(a.approver || a.isSuperadmin || a.permissions.includes('hr.manage'))) return false
    return true
  }).map((w) => w.type)
}

export const defaultTaskFeed = (title = 'Task feed'): TaskFeedFilter => ({ title, workstreamId: null, search: '', assignee: 'anyone', due: 'any' })

/** Today's layout: the panels the dashboard always had; people without them start with their tasks and week. */
export function defaultDashboardLayout(available: readonly DashboardWidgetType[]): DashboardWidget[] {
  const has = (t: DashboardWidgetType) => available.includes(t)
  const pick = (types: DashboardWidgetType[]) => types.filter(has).map((type) => ({ key: type, type, width: dashboardWidgetDef(type).width }))
  const classic = pick(['stats', 'recent_projects'])
  return classic.length ? classic : pick(['my_tasks', 'my_week'])
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {})
const text = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '')

function cleanFeed(v: unknown): TaskFeedFilter {
  const f = obj(v)
  const ws = Number(f.workstreamId)
  return {
    title: text(f.title, 60) || 'Task feed',
    workstreamId: Number.isSafeInteger(ws) && ws > 0 ? ws : null,
    search: text(f.search, 100),
    assignee: f.assignee === 'me' ? 'me' : 'anyone',
    due: f.due === 'overdue' || f.due === 'week' ? f.due : 'any',
  }
}

/**
 * A stored or submitted layout, reduced to what this person may see: unknown or unavailable widgets dropped,
 * duplicate keys and second copies of single widgets dropped, sizes and task-feed filters made valid.
 */
export function cleanDashboardLayout(input: unknown, available: readonly DashboardWidgetType[]): DashboardWidget[] {
  const out: DashboardWidget[] = []
  const keys = new Set<string>()
  const singles = new Set<DashboardWidgetType>()
  let feeds = 0
  for (const raw of Array.isArray(input) ? input : []) {
    const w = obj(raw)
    const type = w.type as DashboardWidgetType
    if (!DASHBOARD_WIDGET_TYPES.includes(type) || !available.includes(type)) continue
    const key = typeof w.key === 'string' && DASHBOARD_KEY.test(w.key) ? w.key : null
    if (!key || keys.has(key)) continue
    const def = dashboardWidgetDef(type)
    if (def.multiple ? feeds >= DASHBOARD_MAX_TASK_FEEDS : singles.has(type)) continue
    if (out.length >= DASHBOARD_MAX_WIDGETS) break
    keys.add(key)
    if (def.multiple) feeds++
    else singles.add(type)
    out.push({ key, type, width: w.width === 'wide' || w.width === 'narrow' ? w.width : def.width, ...(type === 'task_feed' ? { feed: cleanFeed(w.feed) } : {}) })
  }
  return out
}
